import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2, CalendarClock, LineChart, UserCheck } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: UserCheck, text: "A 1-on-1 walkthrough with a real product specialist" },
  { icon: CalendarClock, text: "30 minutes, on your own business numbers" },
  { icon: LineChart, text: "See exactly where AI saves you time on day one" },
];

const EMPTY = { name: "", company: "", mobile: "", email: "", message: "" };

export default function BookDemo() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
      toast.success("Demo request received! We'll reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/10 transition-colors duration-200 placeholder:text-slate-400";

  return (
    <div data-testid="book-demo-page">
      <section className="relative overflow-hidden pt-14 sm:pt-20 pb-24 sm:pb-32">
        <div className="absolute -left-40 top-32 w-[460px] h-[460px] rounded-full bg-brand-redSoft blur-3xl opacity-80 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Reveal y={16}>
              <Eyebrow>Book a Demo</Eyebrow>
            </Reveal>
            <h1 className="mt-5 font-display font-black tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl" data-testid="demo-headline">
              <MaskedLine delay={0.15}>See YesAI on</MaskedLine>
              <MaskedLine delay={0.32}>
                <span className="text-brand-red">your business.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-md">
                Tell us a little about your business. We'll follow up to schedule a free demo — and show you exactly what changes on day one.
              </p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {PERKS.map((p, i) => (
                <Reveal key={p.text} delay={0.6 + i * 0.1}>
                  <div className="flex items-center gap-4" data-testid={`demo-perk-${i}`}>
                    <span className="w-11 h-11 rounded-2xl bg-brand-blueSoft text-brand-blue flex items-center justify-center shrink-0">
                      <p.icon className="w-5 h-5" />
                    </span>
                    <p className="text-sm font-semibold text-slate-700">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.9}>
              <p className="mt-8 text-sm text-slate-500">
                Prefer email? Write to us at{" "}
                <a
                  href="mailto:hi@yesai.co.in"
                  data-testid="demo-email-link"
                  className="font-bold text-brand-red hover:text-brand-redDark transition-colors duration-200"
                >
                  hi@yesai.co.in
                </a>
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.4} y={40}>
            <div className="bg-white border border-slate-200/80 rounded-[2rem] shadow-2xl shadow-slate-900/5 p-7 sm:p-10 relative" data-testid="demo-form-card">
              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="text-center py-10"
                    data-testid="demo-success-panel"
                  >
                    <span className="mx-auto w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8" />
                    </span>
                    <h2 className="mt-6 font-display font-bold text-2xl tracking-tight">Request received!</h2>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">
                      Thanks, {form.name.split(" ")[0] || "there"}. Our team has your details and will reach out on WhatsApp and email shortly to schedule your demo.
                    </p>
                    <button
                      onClick={() => {
                        setDone(false);
                        setForm(EMPTY);
                      }}
                      data-testid="demo-book-another-btn"
                      className="mt-7 text-sm font-bold text-brand-red hover:text-brand-redDark transition-colors duration-200"
                    >
                      Book for someone else
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    onSubmit={submit}
                    className="space-y-5"
                    data-testid="demo-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="demo-name" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Your Name *</label>
                        <input id="demo-name" data-testid="demo-name-input" required value={form.name} onChange={set("name")} placeholder="Ravi Menon" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="demo-company" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Company Name *</label>
                        <input id="demo-company" data-testid="demo-company-input" required value={form.company} onChange={set("company")} placeholder="Metro RMC Pvt Ltd" className={inputCls} />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="demo-mobile" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Mobile Number *</label>
                        <input id="demo-mobile" data-testid="demo-mobile-input" required type="tel" value={form.mobile} onChange={set("mobile")} placeholder="+91 98765 43210" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="demo-email" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Email *</label>
                        <input id="demo-email" data-testid="demo-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="demo-message" className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Tell us about your business</label>
                      <textarea
                        id="demo-message"
                        data-testid="demo-message-input"
                        rows={4}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="We run an RMC plant with 12 trucks and a field sales team of 5..."
                        className={`${inputCls} resize-none`}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="demo-form-submit-btn"
                      className="group w-full inline-flex items-center justify-center gap-2.5 bg-brand-red hover:bg-brand-redDark disabled:opacity-60 text-white font-bold px-7 py-4 rounded-full transition-colors duration-200 shadow-xl shadow-brand-red/25"
                    >
                      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>
                          Request My Demo
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                        </>
                      )}
                    </button>
                    <p className="text-center text-xs text-slate-400">No spam, no obligation. We reply within one business day.</p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
